import { FormHeading, Layout, Logo } from '@vwfs-bronson/bronson-react';
import { FieldError } from '../../../components';
import { FormikButtonGroup } from '../../../components/FormikButtonGroup/FormikButtonGroup';

import { useFormikContext } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MatchMakerAnswers } from '../../../../shared/types';

import {
  carModelList,
  filterModelsByFuelTypes,
  groupModelsByClassification,
  RenderForBrands
} from '../../helpers/vehicle-helpers';
import { appConfig } from '../../../../config';
import { QuestionItem } from '../../types/QuestionItem';

export const CarType: React.FC = () => {
  const { t } = useTranslation('match-maker');
  const { values, setFieldValue, errors, touched } = useFormikContext<MatchMakerAnswers>();
  const selectedFuelTypes = values.fuelTypes ?? [];

  const filteredModelLists = useMemo(
    () => ({
      audi: filterModelsByFuelTypes(carModelList.audi, selectedFuelTypes),
      seat: filterModelsByFuelTypes(carModelList.seat, selectedFuelTypes),
      cupra: filterModelsByFuelTypes(carModelList.cupra, selectedFuelTypes),
      vw: filterModelsByFuelTypes(carModelList.vw, selectedFuelTypes),
      vwn: filterModelsByFuelTypes(carModelList.vwn, selectedFuelTypes)
    }),
    [selectedFuelTypes]
  );

  const availableModelValues = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(filteredModelLists)
            .flat()
            .map((item) => String(item.value))
        )
      ),
    [filteredModelLists]
  );

  useEffect(() => {
    if (appConfig.brandName !== 'audi') {
      setFieldValue('carSearchType', 'ByModel');
    }
  }, [setFieldValue]);

  useEffect(() => {
    const currentValues = values.modelOfCar ?? [];

    const nextValues =
      selectedFuelTypes.length > 0
        ? currentValues.filter((value) => availableModelValues.includes(String(value)))
        : [];

    if (
      currentValues.length !== nextValues.length ||
      currentValues.some((value, index) => value !== nextValues[index])
    ) {
      setFieldValue('modelOfCar', nextValues);
    }
  }, [availableModelValues, selectedFuelTypes, setFieldValue, values.modelOfCar]);

  const getClassificationLabel = (classification: string) =>
    t(`question-pages.items.model.classifications.${classification}.label`);

  const getClassificationDescription = (classification: string) =>
    t(`question-pages.items.model.classifications.${classification}.description`);

  const renderModelGroupSections = (itemList: QuestionItem<string>[], groupIdPrefix: string) => {
    if (selectedFuelTypes.length === 0) return null;

    if (itemList.length === 0) {
      return (
        <p className="u-mt-small u-mb-none">
          {t('question-pages.items.model.no-models-for-selected-fuel')}
        </p>
      );
    }

    return (
      <>
        {groupModelsByClassification(itemList).map(({ classification, items }) => (
          <div key={`${groupIdPrefix}-${classification}`} className="u-mb-large">
            <FormHeading description={getClassificationDescription(classification)}>
              {getClassificationLabel(classification)}
            </FormHeading>
            <FormikButtonGroup
              itemList={items}
              inputType="checkbox"
              propName="modelOfCar"
              required={true}
              preserveValues={true}
              data-testid={`selectableTileGroup-modelOfCar-${groupIdPrefix}-${classification}`}
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      {/* AUDI ONLY */}
      <RenderForBrands audi>
        {selectedFuelTypes.length > 0 && (
          <div data-testid="selectableTileGroup-modelOfCar">
            {renderModelGroupSections(filteredModelLists.audi, 'audi')}
          </div>
        )}
        <Layout>
          <Layout.Item>
            {touched.modelOfCar && errors.modelOfCar && (
              <FieldError
                id="selectableTileGroup-modelOfCar"
                errorMessage={(errors.modelOfCar as string) || ''}
              />
            )}
          </Layout.Item>
        </Layout>
      </RenderForBrands>
      {/* OTHER BRANDS */}
      <RenderForBrands cupra seat vw vwn>
        <RenderForBrands cupra>
          {selectedFuelTypes.length > 0 && (
            <div data-testid="selectableTileGroup-modelOfCar">
              {renderModelGroupSections(filteredModelLists.cupra, 'cupra')}
            </div>
          )}
          <Layout>
            <Layout.Item>
              {touched.modelOfCar && errors.modelOfCar && (
                <FieldError
                  id="selectableTileGroup-modelOfCar"
                  errorMessage={(errors.modelOfCar as string) || ''}
                />
              )}
            </Layout.Item>
          </Layout>
        </RenderForBrands>
        <RenderForBrands vw>
          {selectedFuelTypes.length > 0 && (
            <div data-testid="selectableTileGroup-modelOfCar">
              {renderModelGroupSections(filteredModelLists.vw, 'vw')}
            </div>
          )}
          <Layout>
            <Layout.Item>
              {touched.modelOfCar && errors.modelOfCar && (
                <FieldError
                  id="selectableTileGroup-modelOfCar"
                  errorMessage={(errors.modelOfCar as string) || ''}
                />
              )}
            </Layout.Item>
          </Layout>
        </RenderForBrands>
        <RenderForBrands vwn>
          {selectedFuelTypes.length > 0 && (
            <div data-testid="selectableTileGroup-modelOfCar">
              {renderModelGroupSections(filteredModelLists.vwn, 'vwn')}
            </div>
          )}
          <Layout>
            <Layout.Item>
              {touched.modelOfCar && errors.modelOfCar && (
                <FieldError
                  id="selectableTileGroup-modelOfCar"
                  errorMessage={(errors.modelOfCar as string) || ''}
                />
              )}
            </Layout.Item>
          </Layout>
        </RenderForBrands>
        <RenderForBrands seat>
          {selectedFuelTypes.length > 0 &&
            (filteredModelLists.seat.length > 0 || filteredModelLists.cupra.length > 0 ? (
              <div data-testid="selectableTileGroup-modelOfCar">
                <Layout divider>
                  <Layout.Item className="u-1/2 u-1/1@m">
                    <Logo />
                    <br />
                    {renderModelGroupSections(filteredModelLists.seat, 'seat')}
                  </Layout.Item>
                  <Layout.Item className="u-1/2 u-1/1@m">
                    <Logo
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        scale: '2.5',
                        marginLeft: '103px'
                      }}
                      src="https://cdn.bronson.vwfs.io/cupra/v/11.12.0-beta/img/logo.svg"
                    />
                    <br />
                    {renderModelGroupSections(filteredModelLists.cupra, 'cupra-seat-brand')}
                  </Layout.Item>
                </Layout>
              </div>
            ) : (
              <p className="u-mt-small u-mb-none">
                {t('question-pages.items.model.no-models-for-selected-fuel')}
              </p>
            ))}
          <Layout>
            <Layout.Item>
              {touched.modelOfCar && errors.modelOfCar && (
                <FieldError
                  id="selectableTileGroup-modelOfCar"
                  errorMessage={(errors.modelOfCar as string) || ''}
                />
              )}
            </Layout.Item>
          </Layout>
        </RenderForBrands>
      </RenderForBrands>
    </>
  );
};
